import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportExcelModal = ({ onClose, onImportComplete }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Mapping, 3: Duplicate Check, 4: Import
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [mapping, setMapping] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set()); // For selective import
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const projectFields = [
    { value: 'name', label: 'Название проекта' },
    { value: 'customer_company', label: 'Заказчик' },
    { value: 'industry', label: 'Отрасль' },
    { value: 'stage', label: 'Этап проекта' },
    { value: 'budget_amount', label: 'Бюджет' },
    { value: 'budget_currency', label: 'Валюта' },
    { value: 'city', label: 'Город' },
    { value: 'description', label: 'Описание' },
    { value: 'next_action', label: 'Что делать' },
    { value: 'quarter', label: 'Квартал' },
    { value: 'year', label: 'Год' }
  ];

  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setLoading(true);

    try {
      // Get preview for mapping step first
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/admin/import/projects/preview`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data' 
        }
      });

      if (response.data.success) {
        setPreview(response.data);
        
        // Initialize auto-mapping based on suggestions
        const autoMapping = response.data.auto_mapping.map(col => ({
          excel_column: col.excel_column,
          project_field: col.suggested_field,
          sample_data: col.sample_data
        }));
        setMapping(autoMapping);
        setStep(2); // Go to mapping step first
      }
    } catch (err) {
      console.error('Failed to preview file:', err);
      setError(err.response?.data?.detail || 'Ошибка предпросмотра файла');
    } finally {
      setLoading(false);
    }
  };

  const proceedToDuplicateCheck = async () => {
    setLoading(true);
    try {
      // Check for duplicates after mapping is configured
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/import/projects/check-duplicates`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data' 
        }
      });

      if (response.data.success) {
        setDuplicateCheck(response.data);
        
        // Initialize all rows as selected by default based on actual data
        const totalProjects = response.data.all_projects_data ? 
          response.data.all_projects_data.length : 
          response.data.total_excel_projects;
        
        const allRowNumbers = [];
        for (let i = 0; i < totalProjects; i++) {
          allRowNumbers.push(i + 2); // +2 for header offset
        }
        setSelectedRows(new Set(allRowNumbers));
        
        setStep(3); // Go to duplicate check step
      }
    } catch (err) {
      console.error('Failed to check duplicates:', err);
      setError(err.response?.data?.detail || 'Ошибка проверки дубликатов');
    } finally {
      setLoading(false);
    }
  };
  const proceedWithImport = () => {
    setStep(4); // Go to final import step
  };

  const autoMapColumn = (columnName) => {
    const columnLower = columnName.toLowerCase();
    
    if (columnLower.includes('название') || columnLower.includes('проект')) return 'name';
    if (columnLower.includes('город') || columnLower.includes('локация')) return 'city';
    if (columnLower.includes('отрасль') || columnLower.includes('сектор')) return 'industry';
    if (columnLower.includes('заказчик') || columnLower.includes('застройщик')) return 'customer_company';
    if (columnLower.includes('бюджет') || columnLower.includes('сумма')) return 'budget_amount';
    if (columnLower.includes('валюта')) return 'budget_currency';
    if (columnLower.includes('заметк') || columnLower.includes('комментар')) return 'notes';
    
    return ''; // No mapping
  };

  const updateMapping = (index, field) => {
    const newMapping = [...mapping];
    newMapping[index].project_field = field;
    setMapping(newMapping);
  };

  const executeImport = async () => {
    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping_data', JSON.stringify({
        mappings: mapping.filter(m => m.project_field),
        start_row: 0, // Headers row is 0
        selected_rows: Array.from(selectedRows) // Include selected rows for selective import
      }));

      const response = await axios.post(`${API}/admin/import/projects/execute`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data' 
        }
      });

      onImportComplete(response.data);
      onClose();
    } catch (err) {
      console.error('Failed to import file:', err);
      setError(err.response?.data?.detail || 'Ошибка импорта файла');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-7xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Импорт проектов из Excel
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="close-modal-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mr-2 ${
                step >= 1 ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'
              }`}>
                1
              </div>
              <span className="mr-4">Загрузка файла</span>
            </div>
            
            <div className={`w-16 h-px mx-4 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mr-2 ${
                step >= 2 ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'
              }`}>
                2
              </div>
              <span className="mr-4">Маппинг колонок</span>
            </div>
            
            <div className={`w-16 h-px mx-4 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mr-2 ${
                step >= 3 ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'
              }`}>
                3
              </div>
              <span className="mr-4">Проверка дублей</span>
            </div>
            
            <div className={`w-16 h-px mx-4 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mr-2 ${
                step >= 4 ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'
              }`}>
                4
              </div>
              <span>Импорт</span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: File Upload */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                
                <div className="mt-2 block text-sm font-medium text-gray-900">
                  Выберите Excel файл для импорта
                </div>
                <p className="mt-1 block text-sm text-gray-500">
                  Поддерживаются форматы: .xlsx, .xls
                </p>
                
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  accept=".xlsx,.xls"
                  className="sr-only"
                  onChange={handleFileUpload}
                  data-testid="file-upload-input"
                />
                
                <div className="mt-4">
                  <label
                    htmlFor="file-upload"
                    className="btn-primary inline-flex items-center cursor-pointer"
                    style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10 }}
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Загрузка...
                      </div>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Выбрать файл
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {step === 2 && preview && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-blue-900 mb-2">
                  🔗 Маппинг колонок
                </h3>
                <p className="text-blue-700 text-sm">
                  Найдено {preview.total_rows} строк данных. Настройте соответствие колонок:
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Колонка Excel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Поле проекта
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Пример данных
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mapping.map((map, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {map.excel_column}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={map.project_field}
                            onChange={(e) => updateMapping(index, e.target.value)}
                            className="form-select text-sm w-full"
                            data-testid={`mapping-select-${index}`}
                          >
                            <option value="">-- Не импортировать --</option>
                            {projectFields.map(field => (
                              <option key={field.value} value={field.value}>
                                {field.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                          <div className="truncate" title={map.sample_data}>
                            {map.sample_data ? 
                              String(map.sample_data).substring(0, 40) + (String(map.sample_data).length > 40 ? '...' : '') : 
                              'Нет данных'
                            }
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Sample Data Preview */}
              {preview.sample_data && preview.sample_data.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Пример данных (первые 3 строки):
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr>
                          {preview.columns.map(col => (
                            <th key={col} className="text-left p-2 border-b border-gray-200 font-medium">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sample_data.slice(0, 3).map((row, i) => (
                          <tr key={i}>
                            {preview.columns.map(col => (
                              <td key={col} className="p-2 border-b border-gray-100">
                                {row[col] ? String(row[col]).substring(0, 30) + (String(row[col]).length > 30 ? '...' : '') : '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Enhanced Duplicate Check with Row Selection */}
          {step === 3 && duplicateCheck && (
            <div className="space-y-6">
              <div className="bg-yellow-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-yellow-900 mb-3">
                  🔍 Анализ дубликатов и выбор строк для импорта
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded p-3">
                    <div className="text-2xl font-bold text-blue-600">{duplicateCheck.total_excel_projects}</div>
                    <div className="text-sm text-gray-600">Всего проектов</div>
                  </div>
                  <div className="bg-white rounded p-3">
                    <div className="text-2xl font-bold text-red-600">{duplicateCheck.duplicate_count}</div>
                    <div className="text-sm text-gray-600">Потенциальных дублей</div>
                  </div>
                  <div className="bg-white rounded p-3">
                    <div className="text-2xl font-bold text-green-600">{selectedRows.size}</div>
                    <div className="text-sm text-gray-600">Выбрано для импорта</div>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      const totalProjects = duplicateCheck.all_projects_data ? 
                        duplicateCheck.all_projects_data.length : 
                        duplicateCheck.total_excel_projects;
                      
                      const allRows = new Set();
                      for (let i = 0; i < totalProjects; i++) {
                        allRows.add(i + 2); // +2 for header offset
                      }
                      setSelectedRows(allRows);
                    }}
                    className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    ✅ Выбрать все
                  </button>
                  <button
                    onClick={() => setSelectedRows(new Set())}
                    className="px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                  >
                    ❌ Снять все
                  </button>
                  <button
                    onClick={() => {
                      const totalProjects = duplicateCheck.all_projects_data ? 
                        duplicateCheck.all_projects_data.length : 
                        duplicateCheck.total_excel_projects;
                        
                      const nonDuplicateRows = new Set();
                      const duplicateExcelRows = new Set(duplicateCheck.potential_duplicates.map(d => d.excel_row));
                      for (let i = 0; i < totalProjects; i++) {
                        const rowNumber = i + 2;
                        if (!duplicateExcelRows.has(rowNumber)) {
                          nonDuplicateRows.add(rowNumber);
                        }
                      }
                      setSelectedRows(nonDuplicateRows);
                    }}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    🎯 Только уникальные
                  </button>
                </div>
              </div>

              {/* Projects List with Selection */}
              <div className="bg-white border rounded-lg">
                <div className="px-4 py-3 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-gray-900">Все проекты из Excel файла:</h4>
                    <span className="text-sm text-gray-500">Выберите проекты для импорта</span>
                  </div>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  {(duplicateCheck.all_projects_data || duplicateCheck.sample_data).map((project, index) => {
                    const rowNumber = index + 2; // +2 for header offset
                    const isDuplicate = duplicateCheck.potential_duplicates.some(d => d.excel_row === rowNumber);
                    const duplicateInfo = duplicateCheck.potential_duplicates.find(d => d.excel_row === rowNumber);
                    const isSelected = selectedRows.has(rowNumber);
                    
                    return (
                      <div key={index} className={`px-4 py-4 border-b border-gray-100 last:border-b-0 ${
                        isDuplicate ? 'bg-red-50' : isSelected ? 'bg-green-50' : 'bg-white'
                      }`}>
                        <div className="flex items-start space-x-4">
                          {/* Selection checkbox */}
                          <div className="flex items-center mt-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                const newSelected = new Set(selectedRows);
                                if (e.target.checked) {
                                  newSelected.add(rowNumber);
                                } else {
                                  newSelected.delete(rowNumber);
                                }
                                setSelectedRows(newSelected);
                              }}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                          </div>
                          
                          {/* Row number */}
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Строка {rowNumber}
                            </span>
                          </div>
                          
                          {/* Project info */}
                          <div className="flex-1 min-w-0">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {Object.entries(project).slice(0, 6).map(([key, value]) => 
                                key !== 'row_number' && value ? (
                                  <div key={key} className="text-sm">
                                    <span className="font-medium text-gray-600">{key}:</span>
                                    <span className="text-gray-900 ml-1 block truncate" title={String(value)}>
                                      {String(value)}
                                    </span>
                                  </div>
                                ) : null
                              )}
                            </div>
                          </div>
                          
                          {/* Duplicate indicator */}
                          {isDuplicate && (
                            <div className="flex-shrink-0">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                duplicateInfo?.confidence === 'high' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                🚨 Возможный дубликат
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Duplicate details if any */}
                        {isDuplicate && duplicateInfo && (
                          <div className="mt-3 ml-8 p-3 bg-white rounded border border-red-200">
                            <div className="text-sm">
                              <p className="font-medium text-red-900">Похож на существующий проект:</p>
                              <p className="text-red-700">
                                "{duplicateInfo.existing_project.name}" 
                                {duplicateInfo.existing_project.customer_company && 
                                  ` (${duplicateInfo.existing_project.customer_company})`
                                }
                              </p>
                              <p className="text-xs text-red-600 mt-1">
                                Причина: {duplicateInfo.match_reason} | Уверенность: {duplicateInfo.confidence}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Summary and Actions */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">К импорту:</span> {selectedRows.size} из {duplicateCheck.total_excel_projects} проектов
                    {duplicateCheck.duplicate_count > 0 && (
                      <span className="ml-4 text-red-600">
                        ⚠️ Включено {duplicateCheck.potential_duplicates.filter(d => selectedRows.has(d.excel_row)).length} потенциальных дублей
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => setStep(2)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    ← Вернуться к маппингу
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    disabled={selectedRows.size === 0}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Импортировать выбранные проекты ({selectedRows.size})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Final Import */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <h3 className="text-lg font-medium text-blue-900 mb-2">
                  🚀 Готов к импорту
                </h3>
                <p className="text-blue-700">
                  Будет импортировано {selectedRows.size} проектов из {duplicateCheck?.total_excel_projects || 0}
                </p>
              </div>
              
              {loading && (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Импортирую данные...</p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={step > 1 ? () => setStep(step - 1) : onClose}
              className="btn-secondary"
              disabled={loading}
            >
              {step > 1 ? 'Назад' : 'Отмена'}
            </button>

            {step === 2 && (
              <button
                type="button"
                onClick={proceedToDuplicateCheck}
                className="btn-primary disabled:opacity-50"
                disabled={loading || !mapping.some(m => m.project_field)}
              >
                Проверить дубликаты →
              </button>
            )}
            
            {step === 4 && (
              <button
                type="button"
                onClick={executeImport}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || selectedRows.size === 0}
                data-testid="import-button"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Импорт...
                  </div>
                ) : (
                  `Импортировать ${selectedRows.size} проектов`
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExcelModal;